import CONSTANTS from '../constants/index';
import API from './API';

const ImageService = () => {
  const imageRoot = CONSTANTS.IMAGE_PATH;
  const BLANK_IMAGE = '';
  const THUMBNAIL = {
    fit: 'edges',
    w: 400,
    h: 400,
    auto: 'format',
  };
  const FULL = {
    fit: 'edges',
    w: 1280,
    h: 1280,
    auto: 'compress',
  };
  const PDF_THUMBNAIL = {
    fit: 'edges',
    w: 400,
    h: 400,
    page: 1,
    auto: 'compress',
  };

  return {
    getThumbnail,
    getFull,
    getSource,
    getPDFThumbnail,
    getBlank,
    addTemporaryImage,
  };

  /* ==================== FUNCTIONS ==================== */

  /**
   * Get a thumbnail image
   * @param  {String} image    image to load
   * @param  {Object} options  list of options
   * @return {String}          thumbnail url
   */
  function getThumbnail(image, options = {}) {
    let imageOptions;

    if (image) {
      imageOptions = getUrlParamsForOptions(mergeOptions(THUMBNAIL, options));

      return `${imageRoot + image}?${imageOptions}`;
    }

    return BLANK_IMAGE;
  }

  /**
   * Get full image
   * @param  {String} image    image to load
   * @param  {Object} options  list of options
   * @return {String}          full image url
   */
  function getFull(image, options = {}) {
    let imageOptions;

    if (image) {
      imageOptions = getUrlParamsForOptions(mergeOptions(FULL, options));

      return `${imageRoot}${image}?${imageOptions}`;
    }

    return BLANK_IMAGE;
  }

  /**
   * Get source image
   * @param  {String} image    image to load
   * @param  {Object} options  list of options
   * @return {String}          source image url
   */
  function getSource(image, options = {}) {
    let imageOptions;

    if (image) {
      imageOptions = getUrlParamsForOptions(
        mergeOptions({ auto: 'format' }, options),
      );
      return imageRoot + image + (imageOptions ? `?${imageOptions}` : '');
    }

    return BLANK_IMAGE;
  }

  /**
   * Get image of first page in PDF
   * @param  {String} image    image to load
   * @param  {Object} options  list of options
   * @return {String}          source image url
   */
  function getPDFThumbnail(image, options = {}) {
    let imageOptions;

    if (image) {
      imageOptions = getUrlParamsForOptions(
        mergeOptions(PDF_THUMBNAIL, options),
      );

      return `${imageRoot}${image}?${imageOptions}`;
    }

    return BLANK_IMAGE;
  }

  /**
   * Get blank image
   * @return {String} blank image source
   */
  function getBlank() {
    return BLANK_IMAGE;
  }

  /* ==================== HELPERS ==================== */

  /**
   * Generate url param string for the provided options
   * @param  {Object} options  list of image options
   * @return {String}          query string for all image options
   */
  function getUrlParamsForOptions(options) {
    const pairs = [];

    Object.keys(options).forEach(key => {
      const value = options[key];
      pairs.push(`${key}=${value}`);
    });

    return pairs.join('&');
  }

  /**
   * Merge images options
   * @param  {Object} defaultOptions  list of image options
   * @param  {Object} customOptions  list of image options
   * @param  {Object}          list of merged image options
   */
  function mergeOptions(defaultOptions, customOptions) {
    const merged = Object.assign(defaultOptions, customOptions);

    /* dont allow unsetting w unless there is a height set */
    if (customOptions.w === null && !!customOptions.h) {
      /* revert to default h */
      delete customOptions.w;
    }
    /* dont allow unsetting h unless there is a width set */
    if (customOptions.h === null && !!customOptions.w) {
      /* revert to default h */
      delete customOptions.h;
    }

    Object.keys(merged).forEach(key => {
      if (!merged[key]) {
        delete merged[key];
      }
    });

    /* max height wont work with a fixed height */
    if (merged['max-h']) {
      delete merged.h;
    }

    /* max width wont work with a fixed width */
    if (merged['max-w']) {
      delete merged.w;
    }

    /* max-w or max-h only works with fit 'crop' */
    if (merged['max-h'] || merged['max-w']) {
      merged.fit = 'crop';
    }

    return merged;
  }

  /**
   * Temporary Image Upload
   * @param  {Object} payload  formData with image
   * @param  {Object} customOptions  list of image options
   * @param  {Object}          list of merged image options
   */
  function addTemporaryImage(payload) {
    return API.api.post('/temporary/image?output=json', payload);
  }
};

export default ImageService;
